<template>
  <app-page-wrapper-grid>
    <template v-if="!invalidLink && !successPage">
      <form class="change-password">
        <template v-if="isFormSubmitted && v$.$invalid">
          <app-notifier v-for="(message, index) in errors" :key="index" :type="'failed'" :data="message" />
        </template>
        <div class="change-password__section p-t-20 p-b-20 p-l-20 p-r-20">
          <h2 class="m-b-20">Enter New Password</h2>
          <app-input
            ref="newPassword"
            v-model="v$.newPassword.$model"
            label="New Password"
            type="password"
            :has-error="v$.newPassword.$error && isFormSubmitted"
          />
          <app-input
            ref="confirmPassword"
            v-model="v$.confirmNew.$model"
            label="Confirm New Password"
            type="password"
            :error-message="confirmNewErrorMessage"
            :has-error="v$.confirmNew.$error && isFormSubmitted"
          />
          <app-button
            text="Change Password"
            :aria-label="'Change Password'"
            :handler="submit"
            class="change-password__button m-t-20"
          />
        </div>
      </form>
    </template>
    <template v-if="invalidLink && !successPage">
      <div class="change-password__invalid-section">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 102 102" class="change-password__invalid-section__oval">
          <g fill="none" fill-rule="evenodd">
            <g>
              <g transform="translate(-668 -391) translate(668 391)">
                <circle cx="51" cy="51" r="51" fill="#FFF" />
                <path
                  fill="#FF5757"
                  fill-rule="nonzero"
                  d="M50.5 6C25.923 6 6 25.923 6 50.5S25.923 95 50.5 95 95 75.077 95 50.5c0-11.802-4.688-23.12-13.034-31.466C73.621 10.688 62.302 6 50.5 6zM56 74H45V46h11v28zm0-36H45V27h11v11z"
                  transform="rotate(-180 50.5 50.5)"
                />
              </g>
            </g>
          </g>
        </svg>
        <h4 class="iw-page-title-success change-password__invalid-section__title">Sorry, that link is no longer valid</h4>
        <p class="iw-body-text-large change-password__invalid-section__text">
          To reset your password, return to the login page and select "Forgot Password" to send a new email.
        </p>
      </div>
    </template>
    <template v-if="!invalidLink && successPage">
      <div class="change-password__success-section">
        <svg
          id="Capa_1"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 384.97 384.97"
          style="enable-background: new 0 0 384.97 384.97"
          xml:space="preserve"
          class="change-password__success-section__oval"
        >
          <g>
            <g id="Check_Circle">
              <path
                d="M192.485,0C86.173,0,0,86.173,0,192.485S86.173,384.97,192.485,384.97c106.3,0,192.485-86.185,192.485-192.485
			C384.97,86.173,298.785,0,192.485,0z M192.485,360.909c-93.018,0-168.424-75.406-168.424-168.424S99.467,24.061,192.485,24.061
			s168.424,75.406,168.424,168.424S285.503,360.909,192.485,360.909z"
              />
              <path
                d="M280.306,125.031L156.538,247.692l-51.502-50.479c-4.74-4.704-12.439-4.704-17.179,0c-4.752,4.704-4.752,12.319,0,17.011
			l60.139,58.936c4.932,4.343,12.307,4.824,17.179,0l132.321-131.118c4.74-4.692,4.74-12.319,0-17.011
			C292.745,120.339,285.058,120.339,280.306,125.031z"
              />
            </g>
          </g>
        </svg>
        <h4 class="iw-page-title-success change-password__success-section__title">Success</h4>
        <p class="iw-group-title change-password__success-section__subtitle">Your password has been sucessfully changed</p>
        <p class="iw-body-text-large change-password__success-section__text">You will be redirected to the login page</p>
        <a v-if="showRedirectLink" :href="redirectUri" class="iw-body-text-small change-password__success-section__link"
          >Continue to Login</a
        >
      </div>
    </template>
  </app-page-wrapper-grid>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import useVuelidate from "@vuelidate/core";
import AppPageWrapperGrid from "@/components/base/app-page-wrapper-grid/AppPageWrapperGrid";
import { passwordValidations, REQUIRED_ERR_MSG, INVALID_PASSWORD_ERR_MSG, DIFF_PASSWORD_ERR_MSG } from "../../../../utils";

export default {
  name: "ChangePassword",
  components: {
    AppPageWrapperGrid
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      isFormSubmitted: false,
      newPassword: "",
      confirmNew: "",
      errors: [],
      validatorsCollection: [],
      invalidLink: false,
      successPage: false,
      redirectUri: "",
      showRedirectLink: false
    };
  },
  validations: {
    newPassword: passwordValidations(),
    confirmNew: passwordValidations()
  },
  methods: {
    ...mapActions("user", ["checkForgotPasswordHash", "patchUserPassword", "logOutUser", "authorization"]),
    ...mapGetters("venue", ["getActiveVenueUrl"]),
    isConfirmNewValid() {
      return this.confirmNew === this.newPassword;
    },
    submit() {
      this.isFormSubmitted = false;
      this.errors = [];
      this.v$.$touch();
      this.checkInvalidFields();
      this.isFormSubmitted = true;

      if (!this.v$.$invalid) {
        const { domainID, visitorID } = this.$route.query;
        this.patchUserPassword({
          domainID,
          visitorID,
          password: this.newPassword
        }).then(() => {
          this.successPage = true;
          this.logOutUser();
          setTimeout(() => {
            window.location.href = this.redirectUri;
            this.showRedirectLink = true;
          }, 3000);
        });
      }
    },
    checkInvalidFields() {
      this.validatorsCollection.forEach((item) => {
        if (item.validator.$invalid) {
          if (!this.errors.includes(REQUIRED_ERR_MSG)) {
            this.errors.push(REQUIRED_ERR_MSG);
          }
        }
      });
    }
  },
  computed: {
    confirmNewErrorMessage() {
      if (!this.isConfirmNewValid() && this.isFormSubmitted) {
        return DIFF_PASSWORD_ERR_MSG;
      }
      if ((this.v$.newPassword.$error || this.v$.confirmNew.$error) && this.isFormSubmitted) {
        return INVALID_PASSWORD_ERR_MSG;
      }
      return "";
    }
  },
  mounted() {
    this.validatorsCollection = [
      {
        validator: this.v$.newPassword,
        errorMessage: "New password must be at least 6-symbol length"
      },
      {
        validator: this.v$.confirmNew,
        errorMessage: "Passwords must be identical"
      }
    ];
    this.redirectUri = `${location.origin}/${this.getActiveVenueUrl()}/login`;
    this.checkForgotPasswordHash(this.$route.query)
      .then(() => {
        const { key, email } = this.$route.query;
        this.authorization({
          email: atob(email),
          password: key,
          remember: false
        }).catch((e) => Promise.reject(e.response));
      })
      .catch(() => {
        this.invalidLink = true;
      });
  }
};
</script>

<style scoped lang="scss">
.dark {
  .change-password__section {
    background-color: $background-dark--2;
  }
}

.link {
  outline: none;
  color: $secondary-color;
  border: none;
  background: transparent;

  &:hover {
    text-decoration: underline;
  }
}

.change-password {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 100px auto 20px;
  width: 620px;
  height: 100%;

  &__invalid-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px;
    &__oval {
      width: 80px;
      height: 80px;
      margin-top: 24px;
      margin-bottom: 0px;
    }
    &__title {
      margin-top: 19px;
      margin-bottom: 0px;
    }
    &__text {
      margin-top: 52px;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
      margin-bottom: 0px;
      color: $group-3-color-3;
    }
  }

  &__success-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px;
    &__oval {
      width: 80px;
      height: 80px;
      margin-top: 24px;
      fill: $group-4-color-3;
      margin-bottom: 0px;
    }
    &__title {
      margin-top: 19px;
      margin-bottom: 0px;
    }
    &__subtitle {
      height: 56px;
      margin-top: 28px;
      margin-bottom: 0;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
    }
    &__text {
      margin-top: 52px;
      font-stretch: normal;
      font-style: normal;
      line-height: normal;
      letter-spacing: normal;
      text-align: center;
      margin-bottom: 0px;
      color: $group-3-color-3;
    }
    &__link {
      margin-top: 48px;
      text-align: center;
      margin-bottom: 0px;
      color: $group-2-color-3;
    }
  }

  &__footer {
    width: 100%;
    text-align: left;
  }

  &__section {
    border-radius: 5px;
    width: 400px;

    @include respond-to-mobile {
      width: 80%;
    }
  }

  &__button {
    height: 40px;
  }

  @include respond-to-mobile {
    width: 100%;
    margin-top: 40px;
  }
}
</style>
